import { CollectionItemMoveResult } from '@qkindex/contracts';
import { updateMultipleItems } from '../../firebase/functions';
import { recordKey } from '../collection/multiCollectionIdentity';
import { listItemMoves, moveItems } from './itemMoveService';
import { LocationRequest } from './locationOperations';

export interface LocationOutcome {
  completed: string[];
  pending: string[];
  failed: string[];
  destinationItemIds?: string[];
}

type MoveRequest = Extract<LocationRequest, { kind: 'move' }>;

function moveOutcome(
  request: MoveRequest,
  result: CollectionItemMoveResult,
): LocationOutcome {
  if (
    result.operationId !== request.operationId ||
    result.sourceCollectionId !== request.sourceCollectionId ||
    result.destinationCollectionId !== request.destinationCollectionId ||
    result.destinationLocationId !== request.destinationLocationId ||
    [...result.itemIds].sort().join('\0') !==
      [...request.itemIds].sort().join('\0')
  )
    throw new Error('Move response changed.');
  const keys = (state: 'moved' | 'pending' | 'failed') =>
    result.entries
      .filter((entry) => entry.state === state)
      .map((entry) =>
        recordKey({
          collectionId: request.sourceCollectionId,
          id: entry.sourceItemId,
        }),
      );
  return {
    completed: keys('moved'),
    pending: keys('pending'),
    failed: keys('failed'),
    destinationItemIds: result.entries
      .filter((entry) => entry.state === 'moved')
      .map((entry) => entry.destinationItemId),
  };
}

/** Exactly one transport attempt. The controller alone decides when to retry. */
export async function executeLocationRequest(
  request: LocationRequest,
): Promise<LocationOutcome> {
  if (request.kind === 'move') {
    const { kind, ...payload } = request;
    return moveOutcome(request, await moveItems(payload));
  }
  const { kind, ...payload } = request;
  const { data } = await updateMultipleItems(payload);
  if (
    !data ||
    data.operationId !== request.operationId ||
    data.itemsUpdated !== request.items.length
  ) {
    throw new Error('Location update response changed.');
  }
  return {
    completed: request.items.map((item) =>
      recordKey({ collectionId: request.collectionId, id: item.id }),
    ),
    pending: [],
    failed: [],
  };
}

export async function readLocationMove(
  request: MoveRequest,
): Promise<LocationOutcome | undefined> {
  let cursor: string | undefined;
  const seen = new Set<string>();
  do {
    const page = await listItemMoves(request.sourceCollectionId, cursor);
    const result = page.moves.find(
      (entry) => entry.operationId === request.operationId,
    );
    if (result) return moveOutcome(request, result);
    cursor = page.nextCursor;
    if (cursor && seen.has(cursor))
      throw new Error('Move history cursor repeated.');
    if (cursor) seen.add(cursor);
  } while (cursor);
}

/** Only definitive for the initial attempt; a prior uncertain submission may have committed. */
export function definitiveLocationRejection(error: unknown): boolean {
  return [
    'capacity-exceeded',
    'invalid-request',
    'role-required',
    'membership-required',
    'authentication-required',
    'verification-required',
    'collection-not-found',
    'insufficient-credits',
  ].includes((error as { details?: { code?: string } })?.details?.code || '');
}
